import React, { useMemo, useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { FiChevronDown, FiChevronUp, FiPlus, FiTrash2 } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import CreatableSelect from "react-select/creatable";
import {
  addVideo,
  addVideoQuestions,
  clearSelectedVideo,
  fetchVideoById,
  fetchVideoTags,
  removeTranslatedVideo,
  updateVideo,
} from "../../../store/reducers/videoSectionSlice";
import DraggableSelectList from "../questionsSection/components/QuestionListDropdown";
import { fetchActiveQuestions } from "../../../store/reducers/questionsSectionSlice";
import { fetchCombinedCode } from "../../../store/reducers/countryLanguageSlice";
import FormPageHeader from "../components/FormPageHeader";
import {
  DEFAULT_COUNTRY_LANGUAGE_CODE,
  findDefaultCountryLanguageOption,
  isDefaultCountryLanguageSection,
} from "../../../utils/defaultCountryLanguage";

const THEME_COLOR = "#f85c63";

const parseCombinedCode = (combined_code = "", id = null) => {
  const [countryCode = "", langCode = ""] = combined_code.split("-");
  return {
    value: combined_code,
    label: combined_code,
    id,
    countryCode,
    langCode,
  };
};

const formPanelStyle = {
  background: "#fff",
  border: "1px solid #eef2f7",
  borderRadius: "10px",
  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
  padding: "18px",
  marginBottom: "18px",
};

const sectionCardStyle = {
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: "10px",
  marginBottom: "16px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const sectionHeaderStyle = {
  background: "#f8fafc",
  borderBottom: "1px solid #e2e8f0",
  padding: "12px 16px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const badgeStyle = {
  display: "inline-block",
  background: `${THEME_COLOR}18`,
  color: THEME_COLOR,
  border: `1px solid ${THEME_COLOR}40`,
  borderRadius: "20px",
  padding: "2px 10px",
  fontSize: "12px",
  fontWeight: 600,
};

const DEFAULT_SECTION = (collapsed = false) => ({
  id: Date.now() + Math.random(),
  translatedVideoId: null,
  country_language_id: "",
  combinedCode: "",
  name: "",
  link: "",
  collapsed,
});

const buildDropdownOptions = (items = []) =>
  [...new Set(items.map((item) => `${item}`.trim()).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
    .map((item) => ({
      label: item,
      value: item,
    }));

const ensureOption = (options, value) => {
  const normalizedValue = `${value || ""}`.trim();

  if (!normalizedValue) return options;
  if (options.some((option) => option.value === normalizedValue)) return options;

  return [
    ...options,
    {
      label: normalizedValue,
      value: normalizedValue,
    },
  ];
};

const isValidVideoLink = (value = "") => {
  const trimmedValue = value.trim();

  if (!trimmedValue || /\s/.test(trimmedValue)) return false;

  try {
    new URL(/^https?:\/\//i.test(trimmedValue) ? trimmedValue : `https://${trimmedValue}`);
    return true;
  } catch (error) {
    return false;
  }
};

const VideoAddUpdate = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const params = new URLSearchParams(location.search);
  const videoId = params.get("id");

  const { selectedVideo, singleLoading } = useSelector(
    (state) => state.eduVideos
  );
  const { videoTags } = useSelector((state) => state.eduVideos);
  const { dropdownList = [] } = useSelector((state) => state.countryLanguage);

  const [options, setOptions] = useState([]);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [defaultQuestionIds, setDefaultQuestionIds] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [videoSections, setVideoSections] = useState([]);
  const [removingSectionId, setRemovingSectionId] = useState(null);
  const [formData, setFormData] = useState({
    date: "",
    version: "1",
    tag: "",
    active: true,
    name_en: "",
    link_en: "",
  });
  const [errors, setErrors] = useState({});
  const { activeQuestions } = useSelector((state) => state.questions);
  const combinedCodeOptions = useMemo(
    () =>
      (dropdownList || []).map((item) =>
        parseCombinedCode(
          item.combined_code,
          item.id || item.country_language_id || item.countryLanguageId,
        ),
      ),
    [dropdownList],
  );

  const getCountryLanguageId = (section) =>
    section.country_language_id ||
    combinedCodeOptions.find((option) => option.value === section.combinedCode)?.id ||
    "";

  const usedCountryLanguageIds = videoSections
    .map((section) => `${getCountryLanguageId(section)}`)
    .filter(Boolean);
  const allCountryLanguagesUsed =
    combinedCodeOptions.length > 0 && videoSections.length >= combinedCodeOptions.length;
  const defaultCountryLanguageOption = useMemo(
    () => findDefaultCountryLanguageOption(combinedCodeOptions),
    [combinedCodeOptions],
  );
  const defaultVideoSection = useMemo(
    () =>
      videoSections.find((section) =>
        isDefaultCountryLanguageSection(section, combinedCodeOptions),
      ) || null,
    [combinedCodeOptions, videoSections],
  );
  const defaultVideoName = defaultVideoSection?.name || formData.name_en;
  const defaultVideoLink = defaultVideoSection?.link || formData.link_en;

  const getSectionSelectOptions = (currentSection) => {
    const selectedIds = videoSections
      .filter((section) => section.id !== currentSection.id)
      .map((section) => `${getCountryLanguageId(section)}`)
      .filter(Boolean);

    return combinedCodeOptions.filter((option) => !selectedIds.includes(`${option.id}`));
  };

  useEffect(() => {
    dispatch(fetchActiveQuestions());
    dispatch(fetchCombinedCode());
  }, [dispatch]);

  useEffect(() => {
    if (!defaultCountryLanguageOption) return;

    setVideoSections((prev) => {
      if (
        prev.some((section) =>
          isDefaultCountryLanguageSection(section, combinedCodeOptions),
        )
      ) {
        return prev;
      }

      return [
        {
          ...DEFAULT_SECTION(false),
          country_language_id: defaultCountryLanguageOption.id || "",
          combinedCode: defaultCountryLanguageOption.value,
          name: formData.name_en,
          link: formData.link_en,
        },
        ...prev,
      ];
    });
  }, [
    combinedCodeOptions,
    defaultCountryLanguageOption,
    formData.link_en,
    formData.name_en,
    videoSections,
  ]);

  useEffect(() => {
    if (activeQuestions) {
      const formatted = [...activeQuestions]
        .sort((a, b) => Number(a.id) - Number(b.id))
        .map((q) => ({
          label: `ID# ${q.id} - ${q.question}`,
          value: q.id,
        }));

      setOptions(formatted);
    }
  }, [activeQuestions]);
  
  const handleSelectedIds = (ids) => {
    setSelectedQuestionIds(ids);
  };

  useEffect(() => {
    dispatch(fetchVideoTags());
  }, [dispatch]);

  useEffect(() => {
    setTagOptions((prev) =>
      ensureOption(
        buildDropdownOptions([...(videoTags || []), ...prev.map((item) => item.value)]),
        formData.tag
      )
    );
  }, [videoTags, formData.tag]);

  // FETCH VIDEO
  useEffect(() => {
    if (videoId) {
      dispatch(fetchVideoById(videoId));
    } else {
      setFormData({
        date: new Date().toISOString().split("T")[0],
        version: "v1",
        tag: "",
        active: true,
        name_en: "",
        link_en: "",
      });
      setVideoSections([]);
    }

    return () => dispatch(clearSelectedVideo());
  }, [videoId]);

  // POPULATE
  useEffect(() => {
    if (selectedVideo && videoId) {
      setFormData({
        date: selectedVideo.date_added || "",
        version: selectedVideo.version || "v1",
        tag: selectedVideo.tag || "",
        active: selectedVideo.active ?? true,
        name_en: selectedVideo.name || "",
        link_en: selectedVideo.link || "",
      });

      const savedSections =
        selectedVideo.translations ||
        selectedVideo.translated_videos ||
        selectedVideo.country_sections ||
        [];
      setVideoSections(
        savedSections.map((section) => {
          const option = combinedCodeOptions.find(
            (item) =>
              `${item.id}` === `${section.country_language_id}` ||
              item.value === section.combined_code ||
              item.value === section.combinedCode,
          );
          return {
            ...DEFAULT_SECTION(true),
            id: `video_translation_${section.id || Date.now() + Math.random()}`,
            translatedVideoId: section.id || section.translated_video_id || section.translatedVideoId || null,
            country_language_id: section.country_language_id || option?.id || "",
            combinedCode: section.combinedCode || section.combined_code || option?.value || "",
            name: section.name || section.name_en || "",
            link: section.link || section.url || section.link_en || "",
          };
        }),
      );

      if (selectedVideo.question_ids) {
        setSelectedQuestionIds(selectedVideo.question_ids || []);
        setDefaultQuestionIds(selectedVideo.question_ids || []);
      }

      setTagOptions((prev) => ensureOption(prev, selectedVideo.tag));
    }
  }, [combinedCodeOptions, selectedVideo, videoId]);

  // CHANGE
  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;

    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });

    setErrors({
      ...errors,
      [name]: "",
    });
  };

  const handleTagChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      tag: selectedOption?.value || "",
    }));

    setErrors((prev) => ({
      ...prev,
      tag: "",
    }));
  };

  const handleCreateTag = (inputValue) => {
    const normalizedValue = inputValue.trim();

    if (!normalizedValue) return;

    setTagOptions((prev) => ensureOption(prev, normalizedValue));
    setFormData((prev) => ({
      ...prev,
      tag: normalizedValue,
    }));
    setErrors((prev) => ({
      ...prev,
      tag: "",
    }));
  };

  const addVideoSection = () => {
    if (allCountryLanguagesUsed) return;
    setVideoSections((prev) => [...prev, DEFAULT_SECTION(false)]);
  };

  const removeVideoSection = async (section) => {
    if (isDefaultCountryLanguageSection(section, combinedCodeOptions)) return;

    if (videoId && section.translatedVideoId) {
      setRemovingSectionId(section.id);

      try {
        await dispatch(removeTranslatedVideo(section.translatedVideoId)).unwrap();
        toast.success("Translated video removed successfully");
      } catch {
        toast.error("Translated video remove failed");
        setRemovingSectionId(null);
        return;
      }

      setRemovingSectionId(null);
    }

    setVideoSections((prev) => prev.filter((item) => item.id !== section.id));
  };

  const toggleVideoSection = (sectionId) => {
    setVideoSections((prev) =>
      prev.map((section) =>
        section.id === sectionId ? { ...section, collapsed: !section.collapsed } : section,
      ),
    );
  };

  const updateVideoSection = (sectionId, field, value) => {
    setVideoSections((prev) =>
      prev.map((section) => {
        if (section.id !== sectionId) return section;
        if (field !== "combinedCode") return { ...section, [field]: value };
        if (isDefaultCountryLanguageSection(section, combinedCodeOptions)) return section;
        const selectedOption = combinedCodeOptions.find((option) => option.value === value);
        return {
          ...section,
          combinedCode: value,
          country_language_id: selectedOption?.id || "",
        };
      }),
    );
    setErrors((prev) => {
      const next = { ...prev };
      delete next[`${sectionId}_${field}`];
      return next;
    });
  };

  // VALIDATION
  const validateForm = () => {
    let newErrors = {};
    const hasDefaultSection = videoSections.some((section) =>
      isDefaultCountryLanguageSection(section, combinedCodeOptions),
    );

    if (!defaultCountryLanguageOption || !hasDefaultSection) {
      newErrors.default_country_language = `${DEFAULT_COUNTRY_LANGUAGE_CODE} country/language section is required`;
    }

    // REQUIRED NAME
    if (!defaultVideoName.trim()) {
      newErrors.name_en = "English name is required";
    }

    // REQUIRED + VIMEO VALIDATION (ENGLISH)
    if (!defaultVideoLink.trim()) {
      newErrors.link_en = "English video URL is required";
    } else if (!isValidVideoLink(defaultVideoLink)) {
      newErrors.link_en = "Enter a valid video URL";
    }

    videoSections.forEach((section) => {
      if (!getCountryLanguageId(section)) {
        newErrors[`${section.id}_combinedCode`] = "Please select country language";
      }
      if (!section.name.trim()) {
        newErrors[`${section.id}_name`] = "Name is required";
      }
      if (!section.link.trim()) {
        newErrors[`${section.id}_link`] = "Video URL is required";
      } else if (!isValidVideoLink(section.link)) {
        newErrors[`${section.id}_link`] = "Enter a valid video URL";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // SUBMIT
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      let currentVideoId = videoId;
      const payloadData = {
        ...formData,
        name_en: defaultVideoName,
        link_en: defaultVideoLink,
        translated_videos: videoSections.map((section) => ({
          ...(videoId && section.translatedVideoId ? { id: section.translatedVideoId } : {}),
          country_language_id: Number(getCountryLanguageId(section)),
          name: section.name,
          link: section.link,
        })),
      };

      // ADD VIDEO
      if (!videoId) {
        const res = await dispatch(addVideo(payloadData)).unwrap();

        // handle different API response shapes
        currentVideoId = res?.id || res?.data?.id || res?.video_id;

        if (!currentVideoId) {
          toast.error("Video created but ID not returned");
          return;
        }

        toast.success("Video Added Successfully");
      }
      // UPDATE VIDEO
      else {
        await dispatch(updateVideo({ id: videoId, data: payloadData })).unwrap();
        currentVideoId = videoId;
        toast.success("Video Updated Successfully");
      }

      // ADD QUESTIONS
      if (selectedQuestionIds.length > 0) {
        await dispatch(
          addVideoQuestions({
            video_id: Number(currentVideoId),
            question_ids: selectedQuestionIds,
          })
        ).unwrap();
      }

      history.push("/admin-edu-videos");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong");
    }
  };

  return (
    <div className=" mt-3">
      <FormPageHeader
        title={videoId ? "Update Educational Video" : "Add Educational Video"}
        onBack={() => history.push("/admin-edu-videos")}
      />

      {singleLoading ? (
        <div className="text-center p-5">Loading video...</div>
      ) : (
        <form onSubmit={handleSubmit} className="p-2">
          <div style={formPanelStyle}>
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Date</label>
              <input
                type="date"
                className="form-control"
                value={formData.date}
                disabled
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Version</label>
              <input
                type="text"
                className="form-control"
                name="version"
                value={formData.version}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Tag</label>
              <CreatableSelect
                isClearable
                name="tag"
                options={tagOptions}
                value={
                  formData.tag
                    ? { label: formData.tag, value: formData.tag }
                    : null
                }
                onChange={handleTagChange}
                onCreateOption={handleCreateTag}
                placeholder="Select or type tag"
              />
            </div>

            <div className="col-md-3 d-flex align-items-center">
              <label className="form-label mb-0 me-5">Active</label>

              <div className="form-check form-switch mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  name="active"
                  checked={formData.active}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>
          </div>

          <div style={formPanelStyle}>
          <h6 className="fw-bold mb-3">English Video</h6>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Name (English)*</label>
              <input
                type="text"
                name="name_en"
                className={`form-control ${errors.name_en ? "is-invalid" : ""}`}
                value={defaultVideoName}
                readOnly
              />
              <div className="invalid-feedback">{errors.name_en}</div>
            </div>

            <div className="col-md-6">
              <label className="form-label">Video Link (English)*</label>
              <input
                type="text"
                name="link_en"
                placeholder="https://vimeo.com/123456789"
                className={`form-control ${errors.link_en ? "is-invalid" : ""}`}
                value={defaultVideoLink}
                readOnly
              />
              <div className="invalid-feedback">{errors.link_en}</div>
            </div>
          </div>
          </div>

          <div style={formPanelStyle}>
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <div>
                <h6 className="fw-bold mb-0">Country / Language Videos</h6>
                <small className="text-muted">
                  The {DEFAULT_COUNTRY_LANGUAGE_CODE} section is required and
                  provides the header English video.
                </small>
              </div>
              <button
                type="button"
                className="btn bg_color px-4"
                onClick={addVideoSection}
                disabled={allCountryLanguagesUsed}
              >
                <FiPlus className="me-1" /> Add Section
              </button>
            </div>

            {errors.default_country_language && (
              <div className="text-danger small mb-3">
                {errors.default_country_language}
              </div>
            )}

            {videoSections.length === 0 && (
              <div
                className="text-center text-muted py-4"
                style={{ border: "2px dashed #e2e8f0", borderRadius: 10 }}
              >
                No country-language video sections yet.
              </div>
            )}

            {videoSections.map((section, index) => {
              const parsed = section.combinedCode
                ? parseCombinedCode(section.combinedCode)
                : null;
              const sectionSelectOptions = getSectionSelectOptions(section);

              return (
                <div key={section.id} style={sectionCardStyle}>
                  <div style={sectionHeaderStyle}>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <span className="fw-semibold">Section {index + 1}</span>
                      {parsed && <span style={badgeStyle}>{parsed.countryCode} · {parsed.langCode}</span>}
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <button
                        type="button"
                        className="btn btn-sm p-1"
                        style={{ color: THEME_COLOR, border: 0, lineHeight: 1 }}
                        onClick={() => toggleVideoSection(section.id)}
                        title={section.collapsed ? "Expand" : "Collapse"}
                      >
                        {section.collapsed ? <FiChevronDown size={22} /> : <FiChevronUp size={22} />}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => removeVideoSection(section)}
                        disabled={
                          removingSectionId === section.id ||
                          isDefaultCountryLanguageSection(
                            section,
                            combinedCodeOptions,
                          )
                        }
                      >
                        <FiTrash2 className="me-1" />
                        {removingSectionId === section.id ? "Removing..." : "Remove"}
                      </button>
                    </div>
                  </div>

                  {!section.collapsed && (
                  <div className="p-3">
                    <div className="row g-3">
                      <div className="col-md-4">
                        <label className="form-label">Country · Language*</label>
                        <select
                          className={`form-select ${
                            errors[`${section.id}_combinedCode`] ? "is-invalid" : ""
                          }`}
                          value={section.combinedCode}
                          disabled={isDefaultCountryLanguageSection(
                            section,
                            combinedCodeOptions,
                          )}
                          onChange={(e) =>
                            updateVideoSection(section.id, "combinedCode", e.target.value)
                          }
                        >
                          <option value="">Select country-language...</option>
                          {sectionSelectOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <div className="invalid-feedback">
                          {errors[`${section.id}_combinedCode`]}
                        </div>
                      </div>

                      <div className="col-md-4">
                        <label className="form-label">Name*</label>
                        <input
                          className={`form-control ${errors[`${section.id}_name`] ? "is-invalid" : ""}`}
                          value={section.name}
                          onChange={(e) =>
                            updateVideoSection(section.id, "name", e.target.value)
                          }
                        />
                        <div className="invalid-feedback">{errors[`${section.id}_name`]}</div>
                      </div>

                      <div className="col-md-4">
                        <label className="form-label">Video URL*</label>
                        <input
                          className={`form-control ${errors[`${section.id}_link`] ? "is-invalid" : ""}`}
                          value={section.link}
                          placeholder="https://vimeo.com/123456789"
                          onChange={(e) =>
                            updateVideoSection(section.id, "link", e.target.value)
                          }
                        />
                        <div className="invalid-feedback">{errors[`${section.id}_link`]}</div>
                      </div>
                    </div>
                  </div>
                  )}
                </div>
              );
            })}
          </div>

          <div style={formPanelStyle}>
            <label className="fw-semibold">Select Questions: </label>
            <DraggableSelectList
              options={options}
              defaultValues={defaultQuestionIds}
              onChange={handleSelectedIds}
            />
          </div>

          <div className="mt-4 d-flex justify-content-end">
            <button type="submit" className="btn bg_color px-4">
              {videoId ? "Update Educational Video" : "Add Educational Video"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default VideoAddUpdate;
